/* eslint-disable */
/* ============================================================
   projects-deepdive.jsx
   ------------------------------------------------------------
   Five per-project deep-dive sections, plus the OpenClaw / Hermes
   "agentic workforce" closer. Each has its own visual demo so the
   page reads as a tour, not a repeating template.

   Order on the page:
     03  Commander            (already in components.jsx)
     04  ReadyIQ              (consulting · dashboard demo)
     05  DMHub                (SaaS · WhatsApp chat mock)
     06  MyWiFi Networks      (hospitality · captive portal mock)
     07  Guest Networks       (data layer · sensor query terminal)
     08  OpenClaw + Hermes    (workforce-replacement closer)
   ============================================================ */

import React from 'react';
import { Reveal, AsciiBadge, AsciiRule } from './components.jsx';
import { IconGithub } from './x-feed.jsx';

/* ── KPI inline strip ─────────────────────────────────────────
   Single monospace line: ● stat · stat · stat · deployed Xm ago
   Props:
     stats      — array of strings, e.g. ["22 agents", "454 skills"]
     deployedAt — JS timestamp (ms). Shows "deployed Xm ago" last.
   ─────────────────────────────────────────────────────────── */
function KpiStrip({ stats = [], deployedAt }) {
  const [elapsed, setElapsed] = React.useState(() => deployedAgo(deployedAt));

  React.useEffect(() => {
    if (!deployedAt) return;
    const id = setInterval(() => setElapsed(deployedAgo(deployedAt)), 30_000);
    return () => clearInterval(id);
  }, [deployedAt]);

  const items = deployedAt ? [...stats, elapsed] : stats;

  return (
    <div style={{
      fontFamily: "var(--mono)",
      fontSize: "0.75rem",
      color: "var(--fg-3)",
      letterSpacing: "0.02em",
      marginTop: 8,
      marginBottom: 20,
      display: "flex",
      alignItems: "center",
      gap: 0,
      flexWrap: "wrap",
      lineHeight: 1.5,
    }}>
      <span style={{ color: "var(--green)", marginRight: 6 }} aria-hidden="true">●</span>
      {items.map((s, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span style={{ color: "var(--fg-4)", margin: "0 5px" }}>·</span>}
          <span>{s}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

function deployedAgo(ts) {
  if (!ts) return "";
  const diffMs = Date.now() - ts;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 60) return `deployed ${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `deployed ${diffH}h ago`;
  return `deployed ${Math.floor(diffH / 24)}d ago`;
}

/* shared section head — keeps numbering consistent */
function SectionHead({ num, label, glyph = "diamond" }) {
  return (
    <div className="box-head">
      <AsciiBadge glyph={glyph} count={3} />
      <span>// {num} — {label}</span>
      <span className="rule" />
    </div>
  );
}

/* shared CTA row */
function VisitCTA({ href, label = "visit live site", note }) {
  return (
    <div className="row gap-12 mt-24">
      <a className="btn btn-primary" href={href} target="_blank" rel="noreferrer"
         style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
        ↗ {label}
      </a>
      {note && <span className="dim" style={{ fontSize: 12 }}>{note}</span>}
    </div>
  );
}

/* shared feature list */
function FeatureList({ items }) {
  return (
    <div className="feat-list mt-32">
      {items.map((f, i) => (
        <div className="feat-row" key={i}>
          <span className="glyph">{f.g}</span>
          <div>
            <h3>{f.t}</h3>
            <p>{f.d}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- PROJECTS (A4 row previews + KPI strips) ---------- */
const PROJECTS_A4_ITEMS = [
  {
    tag: "OSS · CLI",
    color: "#F5A623",
    name: "Commander",
    desc: "Master Claude Code instantly. 70 plugin skills · 22 specialist agents · 23 lifecycle hooks. Core free forever.",
    link: "commanderplugin.com",
    anchor: "#commander",
    kpis: [
      { label: "skills", value: "70" },
      { label: "open-source", value: "2 ★" },
      { label: "releases", value: "daily" }
    ],
    previewLabel: "commander preview",
    ascii: `$ commander run fleet
+--------+  +--------+
| codex  |->| claude |
+---+----+  +----+---+
    |            |
+---v----+  +----v---+
| skills |  | ships  |
+--------+  +--------+
[####....] daily
> _`
  },
  {
    tag: "Consulting",
    color: "#7C3AED",
    name: "ReadyIQ",
    desc: "AI readiness, training, and consulting for business leaders.",
    link: "readyiq.ai",
    anchor: "#readyiq",
    kpis: [
      { label: "practice", value: "consulting" },
      { label: "launched", value: "2026-04-24" },
      { label: "clients", value: "accepting" }
    ],
    previewLabel: "readyiq preview",
    ascii: `      __      __
  __/01\\____/02\\__
 /AI\\__/OPS\\__/ROI\\
 \\__/TRAIN\\__/EVAL/
 /04\\____/05\\____/
 \\__/CEO \\__/SHIP/
    \\____/  \\__/
 readiness map
 consult > deploy
 score -> 8.6/10`
  },
  {
    tag: "SaaS · WhatsApp",
    color: "#00DC82",
    name: "DMHub",
    desc: "WhatsApp CRM for bookings, replies, and owner-operator front desks.",
    link: "dmhub.ai",
    anchor: "#dmhub",
    kpis: [
      { label: "channel", value: "WhatsApp CRM" },
      { label: "brands", value: "5 integrated" },
      { label: "status", value: "production" }
    ],
    previewLabel: "dmhub preview",
    ascii: `+--------------------+
| WhatsApp CRM       |
|  + hey table 4?    |
|  | yes 8:15 held   |
|  +----\\            |
+-------\\------------+
 ~ ~ ~ ~ ~ ~ ~ ~ ~
 brands -> inbox
 voice -> booking
 status: production`
  },
  {
    tag: "WiFi Marketing",
    color: "#0096FF",
    name: "MyWiFi Networks",
    desc: "Hospitality WiFi marketing with global resellers in 40+ countries.",
    link: "mywifinetworks.com",
    anchor: "#mywifi",
    kpis: [
      { label: "reach", value: "40+ countries" },
      { label: "surface", value: "captive portals" },
      { label: "status", value: "live" }
    ],
    previewLabel: "mywifi preview",
    ascii: `       .------.
       | AP01 |
       '---+--'
           |
   +-------+-------+
   | router / gw   |
   +---+---+---+---+
       |       |
    phone   portal
  opt-in -> CRM`
  },
  {
    tag: "Physical Spaces",
    color: "#FF1E56",
    name: "Guest Networks",
    desc: "Cybersecurity and WiFi rails for queryable physical spaces.",
    link: "guestnetworks.com",
    anchor: "#guestnetworks",
    kpis: [
      { label: "domain", value: "cybersecurity" },
      { label: "rails", value: "WiFi rails" },
      { label: "status", value: "live" }
    ],
    previewLabel: "guest networks preview",
    ascii: `        /---------\\
       /  GUEST    \\
      |  +-----+    |
      |  |LOCK |    |
      |  +--+--+    |
      |     |       |
      | wifi rails  |
       \\  secure   /
        \\---------/
 sensor -> policy`
  }
];

function ProjectRowsA4Styles() {
  return (
    <style>{`
      .project-table-a4{
        border: 1px solid var(--line-strong);
        background: var(--bg-1);
        overflow: hidden;
      }
      .proj-row.project-row-a4{
        --project-c: var(--fg);
        grid-template-columns: 34px minmax(128px, 178px) minmax(160px, 220px) minmax(220px, 1fr) 28px minmax(220px, 300px);
        grid-template-rows: auto auto;
        gap: 10px 18px;
        align-items: start;
        position: relative;
        color: var(--fg);
        text-decoration: none;
        overflow: hidden;
      }
      .proj-row.project-row-a4:hover,
      .proj-row.project-row-a4:focus-visible{
        background: color-mix(in oklch, var(--project-c) 7%, transparent);
        outline: none;
      }
      .proj-row.project-row-a4:focus-visible{
        outline: 1px solid var(--project-c);
        outline-offset: -1px;
      }
      .proj-row.project-row-a4 .num{
        grid-column: 1;
        grid-row: 1;
        line-height: 1.4;
      }
      .proj-row.project-row-a4 .tag{
        grid-column: 2;
        grid-row: 1;
        --tag-c: var(--project-c);
      }
      .proj-row.project-row-a4 .proj-name{
        grid-column: 3;
        grid-row: 1;
        min-width: 0;
      }
      .proj-row.project-row-a4 .proj-desc{
        grid-column: 4;
        grid-row: 1;
        min-width: 0;
      }
      .proj-row.project-row-a4 .arrow{
        grid-column: 5;
        grid-row: 1;
        align-self: center;
      }
      .proj-row.project-row-a4:hover .proj-name,
      .proj-row.project-row-a4:focus-visible .proj-name,
      .proj-row.project-row-a4:hover .arrow,
      .proj-row.project-row-a4:focus-visible .arrow{
        color: var(--project-c);
      }
      .project-kpi-strip{
        grid-column: 2 / 6;
        grid-row: 2;
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 1px;
        width: 100%;
        min-width: 0;
        border: 1px solid color-mix(in oklch, var(--project-c) 28%, var(--line));
        background: var(--line);
      }
      .project-kpi{
        min-width: 0;
        padding: 7px 9px;
        background: var(--bg-2);
        font-family: var(--mono);
        line-height: 1.2;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .project-kpi-label{
        color: var(--fg-4);
        font-size: 9px;
        font-variant-caps: all-small-caps;
        letter-spacing: 0.18em;
        text-transform: uppercase;
      }
      .project-kpi-value{
        margin-left: 7px;
        color: var(--project-c);
        font-size: 11px;
        font-weight: 700;
      }
      .project-ascii-preview{
        grid-column: 6;
        grid-row: 1 / 3;
        align-self: stretch;
        min-width: 0;
        width: 100%;
        max-width: 300px;
        border: 1px solid color-mix(in oklch, var(--project-c) 36%, var(--line));
        background:
          linear-gradient(180deg, color-mix(in oklch, var(--project-c) 8%, transparent), transparent 40%),
          var(--bg-2);
        padding: 9px 10px;
        opacity: 0;
        transform: translateX(12px);
        transition: opacity 160ms ease, transform 160ms ease, border-color 160ms ease;
        pointer-events: none;
      }
      .proj-row.project-row-a4:hover .project-ascii-preview,
      .proj-row.project-row-a4:focus-visible .project-ascii-preview{
        opacity: 1;
        transform: translateX(0);
        border-color: var(--project-c);
      }
      .project-ascii-title{
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 10px;
        margin-bottom: 6px;
        color: var(--fg-4);
        font-family: var(--mono);
        font-size: 9px;
        letter-spacing: 0.16em;
        text-transform: uppercase;
      }
      .project-ascii-title span:last-child{
        color: var(--project-c);
      }
      .project-ascii-preview pre{
        width: 30ch;
        max-width: 100%;
        margin: 0;
        color: color-mix(in oklch, var(--project-c) 74%, var(--fg));
        font-family: var(--mono);
        font-size: 10px;
        line-height: 1.06;
        letter-spacing: 0;
        white-space: pre;
        overflow: hidden;
      }
      @media (max-width: 960px){
        .proj-row.project-row-a4{
          grid-template-columns: 28px minmax(0, 1fr) 28px;
          grid-template-rows: auto auto auto auto auto;
          gap: 7px 10px;
        }
        .proj-row.project-row-a4 .num{ grid-column: 1; grid-row: 1; align-self: center; }
        .proj-row.project-row-a4 .tag{ grid-column: 2; grid-row: 1; }
        .proj-row.project-row-a4 .proj-name{ grid-column: 2; grid-row: 2; }
        .proj-row.project-row-a4 .proj-desc{ grid-column: 2; grid-row: 3; }
        .proj-row.project-row-a4 .arrow{ grid-column: 3; grid-row: 1; align-self: center; }
        .project-kpi-strip{ grid-column: 2 / 4; grid-row: 4; }
        .project-ascii-preview{
          grid-column: 2 / 4;
          grid-row: 5;
          width: min(100%, 34ch);
          max-width: 100%;
          max-height: 0;
          padding-top: 0;
          padding-bottom: 0;
          opacity: 0;
          transform: translateY(-4px);
          border-color: transparent;
          overflow: hidden;
        }
        .proj-row.project-row-a4:hover .project-ascii-preview,
        .proj-row.project-row-a4:focus-visible .project-ascii-preview{
          max-height: 142px;
          padding-top: 9px;
          padding-bottom: 9px;
          opacity: 1;
          transform: translateY(0);
          border-color: var(--project-c);
        }
      }
      @media (max-width: 700px){
        .project-ascii-preview{ display: none; }
        .project-kpi-strip{
          grid-column: 2 / 4;
          grid-template-columns: 1fr;
          gap: 1px;
        }
        .project-kpi{
          white-space: normal;
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 10px;
        }
      }
      @media (prefers-reduced-motion: reduce){
        .proj-row.project-row-a4,
        .proj-row.project-row-a4 *,
        .project-ascii-preview{
          transition: none !important;
        }
        .project-ascii-preview{
          transform: none !important;
        }
      }
    `}</style>
  );
}

function ProjectKpiStrip({ kpis }) {
  return (
    <div className="project-kpi-strip" aria-label={kpis.map((kpi) => `${kpi.value} ${kpi.label}`).join(" · ")}>
      {kpis.map((kpi) => (
        <span className="project-kpi" key={kpi.label}>
          <span className="project-kpi-label">{kpi.label}</span>
          <span className="project-kpi-value">{kpi.value}</span>
        </span>
      ))}
    </div>
  );
}

function ProjectAsciiPreview({ item }) {
  return (
    <div className="project-ascii-preview" aria-hidden="true">
      <div className="project-ascii-title">
        <span>{item.previewLabel}</span>
        <span>trace</span>
      </div>
      <pre>{item.ascii}</pre>
    </div>
  );
}

/* ---------- DEEP-DIVE INLINE ASCII PREVIEW ---------- */
const DEEPDIVE_ASCII = {
  readyiq: `┌─ readyiq.ai ─────────────┐
│ ◆ audit    [████░░] 92%   │
│ ◆ deploy   [█████░] done  │
│ ◆ train    [███░░░] 68%   │
│ ◇ monitor  [░░░░░░] --    │
│ score ────────────── 8.6  │
└───────────────────────────┘`,

  dmhub: `┌─ dmhub.io ───────────────┐
│ ▸ +1 (305) 555-0142       │
│   "table for 4 tonight?"  │
│                           │
│ ◀ yes — 8:15 held. name?  │
│   AI · confidence 0.94    │
└───────────────────────────┘`,

  mywifi: `┌─ portal.mywifi.io ───────┐
│  ┌─────────────────────┐  │
│  │  Hotel Santorini    │  │
│  │  ▸ free wifi · 60m  │  │
│  │  [Connect with Email│  │
│  │   ▸]                │  │
│  └─────────────────────┘  │
└───────────────────────────┘`,

  guestnetworks: `┌─ query.guestnetworks ────┐
│ SELECT device_count       │
│ FROM   locations          │
│ WHERE  status = 'active'  │
│ ──────────────────────    │
│ rows: 15,247              │
└───────────────────────────┘`,

  openclaw: `┌─ openclaw gateway ───────┐
│ ● alfred    idle  0.2s    │
│ ● hermes    run   4.1s    │
│ ● watchdog  idle  0.0s    │
│ ○ backup    sleep  --     │
│ agents online             │
└───────────────────────────┘`,
};

function DeepDiveAsciiPreview({ project, color }) {
  const art = DEEPDIVE_ASCII[project];
  if (!art) return null;
  return (
    <div aria-hidden="true" style={{
      display: "inline-block",
      marginTop: 4,
      marginBottom: 24,
      padding: "10px 14px",
      border: `1px solid color-mix(in oklch, ${color} 30%, var(--line))`,
      background: `color-mix(in oklch, ${color} 5%, var(--bg-1))`,
      maxWidth: 320,
    }}>
      <pre style={{
        margin: 0,
        fontFamily: "var(--mono)",
        fontSize: "0.72rem",
        lineHeight: 1.55,
        color: `color-mix(in oklch, ${color} 70%, var(--fg-2))`,
        whiteSpace: "pre",
        letterSpacing: 0,
      }}>{art}</pre>
    </div>
  );
}

/* ---------- DEEPDIVE EXPANSION STYLES ---------- */
function DeepDiveStyles() {
  return (
    <style>{`
      .deepdive-how {
        border-top: 1px solid var(--line);
        padding-top: 32px;
      }
      .deepdive-how-label {
        font-family: var(--mono);
        font-size: 10px;
        color: var(--fg-4);
        letter-spacing: 0.22em;
        text-transform: uppercase;
        margin-bottom: 14px;
      }
      .deepdive-flow-caption {
        font-family: var(--mono);
        font-size: 10px;
        color: var(--fg-4);
        letter-spacing: 0.08em;
        margin-bottom: 8px;
      }
      .deepdive-ascii-flow {
        font-family: var(--mono);
        font-size: 11px;
        line-height: 1.45;
        color: var(--fg-2);
        margin: 0;
        white-space: pre;
        overflow-x: auto;
        padding: 18px 20px;
        background: var(--bg-1);
        border: 1px solid var(--line-strong);
        scrollbar-width: thin;
        scrollbar-color: var(--line-strong) transparent;
      }
      .deepdive-ascii-flow::-webkit-scrollbar {
        height: 4px;
      }
      .deepdive-ascii-flow::-webkit-scrollbar-track {
        background: transparent;
      }
      .deepdive-ascii-flow::-webkit-scrollbar-thumb {
        background: var(--line-strong);
      }
      .deepdive-value {
        font-family: var(--mono);
        font-size: 12px;
        color: var(--fg-2);
        border: 1px solid var(--line-strong);
        border-top: 1px dashed var(--line);
        background: var(--bg-2);
        padding: 10px 20px;
        line-height: 1.6;
      }
      .deepdive-value .vk {
        color: var(--amber);
        font-weight: 600;
      }
      .deepdive-longform {
        display: grid;
        gap: 22px;
        max-width: 72ch;
        padding-top: 4px;
      }
      .deepdive-longform p {
        margin: 0;
        font-size: 14px;
        line-height: 1.75;
        color: var(--fg-2);
      }
      .deepdive-longform p + p {
        border-top: 1px dashed var(--line);
        padding-top: 22px;
      }
      @media (max-width: 768px) {
        .deepdive-ascii-flow {
          font-size: 9px;
          padding: 12px 14px;
        }
      }
    `}</style>
  );
}

/* ---------- TAB DATA (matches deep-dive IDs) ---------- */
const STACK_TABS = [
  { id: "commander",    label: "Commander",      color: "#FF6B47" },
  { id: "readyiq",      label: "ReadyIQ",         color: "#7C3AED" },
  { id: "dmhub",        label: "DMHub",           color: "#00DC82" },
  { id: "mywifi",       label: "MyWiFi",          color: "#0096FF" },
  { id: "guestnetworks",label: "Guest Networks",  color: "#FF1E56" },
  { id: "openclaw",     label: "OpenClaw",        color: "#F5A623" },
];

function StackTabStyles() {
  return (
    <style>{`
      .stack-tabs{
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        align-items: center;
        margin-top: 28px;
      }
      .stack-tab{
        display: inline-flex;
        align-items: center;
        gap: 7px;
        padding: 7px 14px 7px 10px;
        border: 1px solid var(--line-strong);
        background: var(--bg-1);
        font-family: var(--mono);
        font-size: 12px;
        letter-spacing: 0.04em;
        color: var(--fg-3);
        cursor: pointer;
        transition: border-color 120ms, color 120ms, background 120ms;
        user-select: none;
        text-decoration: none;
      }
      .stack-tab:hover{
        border-color: var(--tab-c, var(--amber));
        color: var(--fg);
      }
      .stack-tab.active{
        border-color: var(--tab-c, var(--amber));
        background: color-mix(in oklch, var(--tab-c, var(--amber)) 10%, var(--bg-1));
        color: var(--fg);
      }
      .stack-tab .tab-dot{
        display: inline-block;
        width: 8px;
        height: 8px;
        border-left: 3px solid var(--tab-c, var(--amber));
        flex-shrink: 0;
      }
      .stack-tab-viewall{
        margin-left: 4px;
        font-family: var(--mono);
        font-size: 12px;
        color: var(--fg-4);
        letter-spacing: 0.12em;
        background: none;
        border: none;
        border-left: 1px dashed var(--line-strong);
        cursor: pointer;
        padding: 4px 6px 4px 14px;
        text-decoration: none;
        transition: color 120ms, border-color 120ms;
      }
      .stack-tab-viewall:hover{ color: var(--fg-2); }
      .stack-tab-viewall.active{ color: var(--amber); }
      @media (max-width: 768px){
        .stack-tabs{ row-gap: 8px; }
        .stack-tab{ font-size: 11px; padding: 6px 10px 6px 8px; }
      }
    `}</style>
  );
}

function ProjectsTabbed({ activeProject, setActiveProject }) {
  return (
    <section id="projects" data-section="projects" className="section-pad">
      <StackTabStyles />
      <DeepDiveStyles />
      <div className="container">
        <div className="box-head">
          <AsciiBadge glyph="block" count={3} />
          <span>// 02 — the stack</span>
          <span className="rule" />
        </div>

        <Reveal>
          <div>
            <h2 className="h-section">5 brands. AI agent teams. one operator.</h2>
            <p className="dim2 mt-12" style={{ fontSize: 14, maxWidth: 540 }}>
              Each linked out to its own home. kevinz.ai is the hub.
            </p>
          </div>
        </Reveal>

        <Reveal delay={80}>
          <nav className="stack-tabs" aria-label="Company selector">
            {STACK_TABS.map((tab) => (
              <button
                key={tab.id}
                className={"stack-tab" + (activeProject === tab.id ? " active" : "")}
                style={{ "--tab-c": tab.color }}
                onClick={() => setActiveProject(tab.id)}
                aria-pressed={activeProject === tab.id}
              >
                <span className="tab-dot" aria-hidden="true" />
                {tab.label}
              </button>
            ))}
            <button
              className={"stack-tab-viewall" + (activeProject === "all" ? " active" : "")}
              onClick={() => setActiveProject("all")}
            >
              [ ─ stack all 6 ─ ]
            </button>
          </nav>
        </Reveal>
      </div>
    </section>
  );
}

/* ============================================================
   04 — ReadyIQ · AI consulting & training (dashboard demo)
   ============================================================ */
function ReadyIQ({ activeProject }) {
  if (activeProject !== "all" && activeProject !== "readyiq") return null;
  const chapterMarker = activeProject === "all"
    ? <AsciiRule num="04" label="readyiq" />
    : null;
  const features = [
    { g: "[01]", t: "AI Audit", d: "2-day diagnostic · 6-dimension scorecard." },
    { g: "[02]", t: "Implementation Sprint", d: "3–5 production workflows in 10 weeks." },
    { g: "[03]", t: "Fractional AI Lead", d: "Embedded AI executive." },
    { g: "[04]", t: "ReadyIQ Platform", d: "Self-serve AI tool suite · free scorecard · pricing at readyiq.ai." }
  ];

  return (
    <>
      {chapterMarker}
      <section id="readyiq" data-section="readyiq" data-screen-label="04 ReadyIQ" className="section-pad"
        style={{ "--amber": "#7C3AED", "--amber-d": "#5b25c9" }}>
      <div className="container">
        <SectionHead num="04" label="readyiq · ai consulting" glyph="diamond" />
        <KpiStrip
          stats={["free readiness scorecard", "10-week sprints", "40+ orgs", "$5M–$500M businesses"]}
          deployedAt={1778198400000}
        />
        <DeepDiveAsciiPreview project="readyiq" color="#7C3AED" />

        <Reveal>
          <div className="cmd-grid">
            <div>
              <h2 className="h-section">
                ReadyIQ.<br />
                <span className="dim2" style={{ fontWeight: 600 }}>{">"} AI deployment for non-technical CEOs.</span>
              </h2>
              <p className="lede mt-20" style={{ maxWidth: "48ch" }}>
                Not another strategy deck. Working agents shipped, your team trained, in 30 days.
              </p>

              <VisitCTA href="https://www.readyiq.ai/book" label="readyiq.ai" note="· book an AI Audit →" />

              <FeatureList items={features} />
            </div>

            <Reveal delay={120}>
              <ReadyIQDashboard />
            </Reveal>
          </div>
        </Reveal>

        {/* ── How it works ─────────────────────────────────── */}
        <Reveal delay={60}>
          <div className="deepdive-how" style={{ marginTop: 52 }}>
            <div className="deepdive-how-label">── how it works ───────────────────────────────</div>
            <div className="deepdive-flow-caption">┌─ score it ────── build it ────── compound it ─┐</div>
            <pre className="deepdive-ascii-flow">{`
  ╭─────────────────────╮     ╭──────────────────────╮     ╭──────────────────────╮
  │  WHERE YOU ARE      │     │  READINESS AUDIT     │     │  YOUR MAP            │
  │  ─────────────────  │     │  ──────────────────  │     │  ──────────────────  │
  │  Free scorecard     │────▶│  6-dimension audit   │────▶│  Score card 0–10     │
  │  Exec interviews    │     │  Risk triage         │     │  Gap heatmap         │
  │  Tool inventory     │     │  Quick-win ranking   │     │  90-day roadmap      │
  ╰─────────────────────╯     ╰──────────────────────╯     ╰──────────────────────╯
           │                           │                            │
  ╭─────────────────────╮     ╭──────────────────────╮     ╭──────────────────────╮
  │  YOUR MAP           │     │  10-WEEK SPRINT      │     │  AGENTS LIVE         │
  │  ─────────────────  │     │  ──────────────────  │     │  ──────────────────  │
  │  Top 3 workflows    │────▶│  Build, test, ship   │────▶│  3–5 workflows live  │
  │  Success criteria   │     │  Team trained live   │     │  Playbooks yours     │
  │  Named owners       │     │  Iteration loops     │     │  Hours tracked       │
  ╰─────────────────────╯     ╰──────────────────────╯     ╰──────────────────────╯
           │                           │                            │
  ╭─────────────────────╮     ╭──────────────────────╮     ╭──────────────────────╮
  │  AGENTS LIVE        │     │  KEEP COMPOUNDING    │     │  THE PAYOFF          │
  │  ─────────────────  │     │  ──────────────────  │     │  ──────────────────  │
  │  Weekly metrics     │────▶│  Fractional AI lead  │────▶│  ROI documented      │
  │  New workflows      │     │  Platform tools      │     │  Score climbs        │
  │  Exec reviews       │     │  Quarterly roadmap   │     │  Team self-serve     │
  ╰─────────────────────╯     ╰──────────────────────╯     ╰──────────────────────╯`}
            </pre>
            <div className="deepdive-value"><span className="vk">▸ why it matters</span> — you stop guessing where AI fits. The free scorecard names your workflows; the 10-week sprint ships them.</div>
          </div>
        </Reveal>

        {/* ── Longform ─────────────────────────────────────── */}
        <Reveal delay={80}>
          <div className="deepdive-longform" style={{ marginTop: 40 }}>
            <p>
              ReadyIQ started because every business owner asking "how do I use AI?" was getting the same useless answer: a strategy slide deck with no working code attached. The real problem was never awareness — executives know AI exists. The gap was translation: nobody was showing a non-technical CEO exactly which of their current workflows to hand to an agent, in what order, with what success criteria. That gap cost them six months of false starts.
            </p>
            <p>
              A manufacturing client in Q1 2026 ran through the full program. After the readiness audit, three workflows ranked in the top tier: supplier quote follow-ups, inbound inquiry triage, and weekly ops reporting. All three had agents in production inside one sprint. The supplier workflow alone cut 22 hours per week of manual email work. Their readiness score moved from 5.1 to 8.6 — not because they got smarter, but because they finally had a clear map.
            </p>
            <p>
              The self-serve scorecard is now live — free, no call required, at readyiq.ai. The next 12 months focus on scale: cohort licensing so enterprise clients can run internal programs, and a consulting track aimed specifically at finance, professional services, franchises, and manufacturing — the $5M–$500M businesses where the AI literacy gap is sharpest and the hourly savings per agent are highest.
            </p>
          </div>
        </Reveal>

        {/* ── KPI strip ────────────────────────────────────── */}
        <Reveal delay={100}>
          <div style={{ marginTop: 36 }}>
            <ProjectKpiStrip kpis={[
              { label: "clients",       value: "40+" },
              { label: "hrs/wk saved",  value: "184" },
              { label: "readiness avg", value: "8.6" },
              { label: "sprint",        value: "10wk" }
            ]} />
          </div>
        </Reveal>
      </div>
    </section>
    </>
  );
}

/* small dashboard mock — KPI tiles + horizontal bars */
function ReadyIQDashboard() {
  return (
    <div className="win" style={{ background: "var(--bg-1)" }}>
      <div className="win-title">
        <div className="lights"><span className="r" /><span className="a" /><span className="g" /></div>
        <span>readyiq · client portal</span>
        <span className="meta">Q2 · 2026</span>
      </div>
      <div style={{ padding: 18 }}>
        {/* KPI row */}
        <div className="deepdive-kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, background: "var(--line)", border: "1px solid var(--line-strong)" }}>
          {[
            { k: "AGENTS LIVE",     v: "12",     d: "across 4 depts" },
            { k: "HOURS / WK SAVED", v: "184",    d: "avg per client" },
            { k: "READINESS SCORE",  v: "8.6",    d: "/ 10 · +1.4 mom" }
          ].map((kpi, i) => (
            <div key={i} style={{ background: "var(--bg-1)", padding: "14px 14px 12px" }}>
              <div style={{ fontSize: 9, color: "var(--fg-4)", letterSpacing: "0.22em", textTransform: "uppercase" }}>{kpi.k}</div>
              <div style={{ fontFamily: "var(--mono)", fontWeight: 700, fontSize: 22, color: "var(--amber)", marginTop: 4, letterSpacing: "-0.04em" }}>{kpi.v}</div>
              <div style={{ fontSize: 10, color: "var(--fg-3)", marginTop: 2 }}>{kpi.d}</div>
            </div>
          ))}
        </div>

        {/* readiness pillars */}
        <div style={{ marginTop: 18, fontSize: 10, color: "var(--fg-4)", letterSpacing: "0.22em", textTransform: "uppercase", marginBottom: 10 }}>
          ── readiness pillars ───────────────────
        </div>
        {[
          { label: "Data hygiene",     pct: 92, status: "ok" },
          { label: "Tooling parity",   pct: 81, status: "ok" },
          { label: "Workflow clarity", pct: 74, status: "ok" },
          { label: "Security posture", pct: 63, status: "warn" },
          { label: "Eval discipline",  pct: 56, status: "warn" },
          { label: "Org velocity",     pct: 38, status: "fix" }
        ].map((row, i) => {
          const c = row.status === "ok" ? "var(--green)" : row.status === "warn" ? "var(--amber)" : "var(--red)";
          return (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "minmax(80px, 120px) 1fr 48px 36px", gap: 6, alignItems: "center", padding: "5px 0", fontSize: 11, minWidth: 0 }}>
              <span style={{ color: "var(--fg-2)" }}>{row.label}</span>
              <div style={{ height: 8, background: "var(--bg-3)", border: "1px solid var(--line)", position: "relative" }}>
                <div style={{ position: "absolute", inset: 0, width: row.pct + "%", background: c, opacity: 0.85 }} />
              </div>
              <span style={{ fontFamily: "var(--mono)", color: c, textAlign: "right" }}>{row.pct}%</span>
              <span style={{ fontSize: 9, color: c, letterSpacing: "0.18em", textTransform: "uppercase", textAlign: "right" }}>{row.status}</span>
            </div>
          );
        })}

        <div style={{ marginTop: 14, padding: "10px 0 0", borderTop: "1px dashed var(--line)", display: "grid", gridTemplateColumns: "1fr auto", gap: 10, fontSize: 10, color: "var(--fg-4)", letterSpacing: "0.18em", textTransform: "uppercase" }}>
          <span>▸ start here · free readiness scorecard</span>
          <span style={{ color: "var(--amber)" }}>readyiq.ai →</span>
        </div>
      </div>
    </div>
  );
}


/* ============================================================
   05 — DMHub · WhatsApp-first SaaS (chat mock)
   ============================================================ */
function DMHub({ activeProject }) {
  if (activeProject !== "all" && activeProject !== "dmhub") return null;
  const chapterMarker = activeProject === "all"
    ? <AsciiRule num="05" label="dmhub" />
    : null;
  const features = [
    { g: "[01]", t: "WhatsApp-First", d: "SMS, IG, Messenger as fallbacks." },
    { g: "[02]", t: "AI Personality", d: "Sounds like your best front-desk hire." },
    { g: "[03]", t: "Voice + Booking", d: "Calls answered, transcribed, booked." },
    { g: "[04]", t: "SMB-Native Pricing", d: "Flat pricing. No per-seat gotchas. See dmhub.ai/pricing." }
  ];

  return (
    <>
      {chapterMarker}
      <section id="dmhub" data-section="dmhub" data-screen-label="05 DMHub" className="section-pad"
        style={{ "--amber": "#00DC82", "--amber-d": "#00a563" }}>
        <div className="container">
        <SectionHead num="05" label="dmhub · whatsapp ai for smbs" glyph="block" />
        <KpiStrip
          stats={["800+ integrations", "12K MAU", "99.9% uptime"]}
          deployedAt={1778194800000}
        />
        <DeepDiveAsciiPreview project="dmhub" color="#00DC82" />

        <Reveal>
          <div className="cmd-grid">
            <div>
              <h2 className="h-section">
                DMHub.<br />
                <span className="dim2" style={{ fontWeight: 600 }}>{">"} the AI front desk built for WhatsApp.</span>
              </h2>
              <p className="lede mt-20" style={{ maxWidth: "48ch" }}>
                Books, confirms, recovers — on WhatsApp, 24/7. The AI front desk owner-operators actually trust.
              </p>

              <VisitCTA href="https://www.dmhub.ai/pricing" label="dmhub.ai/pricing" note="· start free →" />

              <FeatureList items={features} />
            </div>

            <Reveal delay={120}>
              <DMHubChat />
            </Reveal>
          </div>
        </Reveal>

        {/* ── How it works ─────────────────────────────────── */}
        <Reveal delay={60}>
          <div className="deepdive-how" style={{ marginTop: 52 }}>
            <div className="deepdive-how-label">── how it works ───────────────────────────────</div>
            <div className="deepdive-flow-caption">┌─ guest ────── ai ────── booked ─┐</div>
            <pre className="deepdive-ascii-flow">{`
  ╭─────────────────────╮     ╭──────────────────────╮     ╭──────────────────────╮
  │  GUEST TEXTS YOU    │     │  AI UNDERSTANDS      │     │  BOOKED · DONE       │
  │  ─────────────────  │     │  ──────────────────  │     │  ──────────────────  │
  │  WhatsApp message   │────▶│  Intent classifier   │────▶│  Booking confirmed   │
  │  SMS / IG DM        │     │  Confidence score    │     │  Calendar updated    │
  │  Voice call         │     │  Channel match       │     │  Receipt sent        │
  ╰─────────────────────╯     ╰──────────────────────╯     ╰──────────────────────╯
           │                           │                            │
  ╭─────────────────────╮     ╭──────────────────────╮     ╭──────────────────────╮
  │  REAL QUESTIONS     │     │  ON-BRAND REPLIES    │     │  UNDER 4 SECONDS     │
  │  ─────────────────  │     │  ──────────────────  │     │  ──────────────────  │
  │  "Hey table for 4?" │────▶│  Brand voice tuned   │────▶│  Replies in <4s      │
  │  "Change my booking"│     │  Tone per venue      │     │  0.94 avg confidence │
  │  "Any deals on?"    │     │  Escalate at 0.60    │     │  Human if needed     │
  ╰─────────────────────╯     ╰──────────────────────╯     ╰──────────────────────╯
           │                           │                            │
  ╭─────────────────────╮     ╭──────────────────────╮     ╭──────────────────────╮
  │  REVENUE AT RISK    │     │  AUTO RECOVERY       │     │  FULL VISIBILITY     │
  │  ─────────────────  │     │  ──────────────────  │     │  ──────────────────  │
  │  No-show detected   │────▶│  Reminder sequence   │────▶│  Thread analytics    │
  │  Abandoned booking  │     │  Win-back message    │     │  Channel breakdown   │
  │  Negative sentiment │     │  Offer trigger       │     │  Confidence trends   │
  ╰─────────────────────╯     ╰──────────────────────╯     ╰──────────────────────╯`}
            </pre>
            <div className="deepdive-value"><span className="vk">▸ why it matters</span> — a missed DM is a lost booking. Answering in 4 seconds at 2am is revenue your staff never sees.</div>
          </div>
        </Reveal>

        {/* ── Longform ─────────────────────────────────────── */}
        <Reveal delay={80}>
          <div className="deepdive-longform" style={{ marginTop: 40 }}>
            <p>
              DMHub grew from a single frustration: owner-operators at restaurants and salons were losing bookings every night because nobody was watching the WhatsApp inbox after 6pm. The reservation request would come in at 8:47, sit unanswered, and the guest would book somewhere else before morning. Every SMB owner knew it was happening. Nobody had a flat-priced fix that didn't require a hiring cycle.
            </p>
            <p>
              A restaurant group running four locations in Miami onboarded in February 2026. Aida — their DMHub agent persona — handled 94% of inbound threads within the first week without human escalation. The team clocked 3 minutes to onboard each location: connect WhatsApp Business, paste their menu PDF, set two escalation rules. Within 30 days the group recovered an estimated 38 bookings that would have otherwise dropped. The kitchen coordinator said it felt like having a night-shift front desk that never called in sick.
            </p>
            <p>
              The roadmap over the next year expands beyond restaurants into any booking-heavy SMB: salons, gyms, med spas, boutique hotels. Voice call transcription and booking confirmation is in beta. The next major feature is a cross-location inbox view, so a group owner can see every thread across all their venues in a single dashboard without switching accounts.
            </p>
          </div>
        </Reveal>

        {/* ── KPI strip ────────────────────────────────────── */}
        <Reveal delay={100}>
          <div style={{ marginTop: 36 }}>
            <ProjectKpiStrip kpis={[
              { label: "pricing",       value: "dmhub.ai/pricing" },
              { label: "channels",      value: "6" },
              { label: "avg confidence",value: "0.94" },
              { label: "onboarding",    value: "3 min" }
            ]} />
          </div>
        </Reveal>
      </div>
    </section>
    </>
  );
}

/* WhatsApp-style chat mock — green bubbles + agent typing */
function DMHubChat() {
  const messages = [
    { who: "them", t: "20:42", body: "Hey do you have any tables for 4 tonight at 8?" },
    { who: "ai",   t: "20:42", body: "Hi! Yes — one 4-top open at 8:15. Want me to hold it?", tag: "Aida · DMHub" },
    { who: "them", t: "20:43", body: "Yes please. Anniversary, can we do something nice?" },
    { who: "ai",   t: "20:43", body: "Booked under Sarah · 8:15 · 4 guests. I've added a note for the kitchen and prepped a complimentary dessert plate. 🎉", tag: "Aida · DMHub" },
    { who: "them", t: "20:44", body: "You're a lifesaver" }
  ];
  return (
    <div className="win" style={{ background: "var(--bg-1)" }}>
      <div className="win-title">
        <div className="lights"><span className="r" /><span className="a" /><span className="g" /></div>
        <span>dmhub · live thread · +1 (305) 555-0142</span>
        <span className="meta">RES · 0114</span>
      </div>
      <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 10, background: "var(--bg-2)" }}>
        {messages.map((m, i) => (
          <div key={i} style={{
            alignSelf: m.who === "them" ? "flex-start" : "flex-end",
            maxWidth: "78%",
            background: m.who === "them" ? "var(--bg-3)" : "color-mix(in oklch, var(--green) 22%, var(--bg-3))",
            border: "1px solid " + (m.who === "them" ? "var(--line)" : "color-mix(in oklch, var(--green) 30%, var(--line))"),
            padding: "8px 12px",
            fontSize: 12.5, lineHeight: 1.45, color: "var(--fg)",
            position: "relative"
          }}>
            {m.tag && (
              <div style={{ fontSize: 9, color: "var(--green)", letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 4 }}>
                ◆ {m.tag}
              </div>
            )}
            <div>{m.body}</div>
            <div style={{ fontSize: 9, color: "var(--fg-4)", marginTop: 4, textAlign: m.who === "them" ? "left" : "right", letterSpacing: "0.05em" }}>
              {m.t}{m.who === "ai" ? " · ✓✓ delivered" : ""}
            </div>
          </div>
        ))}

        {/* typing indicator */}
        <div style={{
          alignSelf: "flex-start",
          background: "var(--bg-3)", border: "1px solid var(--line)",
          padding: "6px 12px",
          fontFamily: "var(--mono)", fontSize: 11, color: "var(--fg-3)",
          display: "inline-flex", alignItems: "center", gap: 6
        }}>
          <span>they're typing</span>
          <span className="blink">●●●</span>
        </div>
      </div>
      <div className="win-foot" style={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", gap: 10 }}>
        <span>▸ ai handled · escalate at confidence &lt; 0.6</span>
        <span style={{ color: "var(--green)" }}>0.94 confidence</span>
      </div>
    </div>
  );
}


/* ============================================================
   06 — MyWiFi Networks · global hospitality (captive portal)
   ============================================================ */
function MyWiFi({ activeProject }) {
  if (activeProject !== "all" && activeProject !== "mywifi") return null;
  const chapterMarker = activeProject === "all"
    ? <AsciiRule num="06" label="mywifi" />
    : null;
  const features = [
    { g: "[01]", t: "Global Reseller Network", d: "40+ countries since 2010." },
    { g: "[02]", t: "Captive Portal Engine", d: "Branded splash, social login, vouchers." },
    { g: "[03]", t: "Marketing Layer", d: "Email + SMS + WhatsApp re-engagement." },
    { g: "[04]", t: "Hardware-Agnostic", d: "Cisco, Aruba, UniFi, Ruckus, MikroTik." }
  ];

  return (
    <>
      {chapterMarker}
      <section id="mywifi" data-section="mywifi" data-screen-label="06 MyWiFi Networks" className="section-pad"
        style={{ "--amber": "#0096FF", "--amber-d": "#0070cc" }}>
        <div className="container">
          <SectionHead num="06" label="mywifi networks · hospitality wifi marketing" glyph="bracket" />
          <KpiStrip
            stats={["28K MAU", "$42K MRR", "4M+ end users"]}
            deployedAt={1778191200000}
          />
          <DeepDiveAsciiPreview project="mywifi" color="#0096FF" />

        <Reveal>
          <div className="cmd-grid">
            <div>
              <h2 className="h-section">
                MyWiFi Networks.<br />
                <span className="dim2" style={{ fontWeight: 600 }}>{">"} white-label WiFi marketing platform.</span>
              </h2>
              <p className="lede mt-20" style={{ maxWidth: "48ch" }}>
                Hospitality WiFi marketing in 40+ countries. ROI from week one.
              </p>

              <VisitCTA href="https://mywifinetworks.com" label="mywifinetworks.com" note="· become a reseller →" />

              <FeatureList items={features} />
            </div>

            <Reveal delay={120}>
              <CaptivePortal />
            </Reveal>
          </div>
        </Reveal>

        {/* ── How it works ─────────────────────────────────── */}
        <Reveal delay={60}>
          <div className="deepdive-how" style={{ marginTop: 52 }}>
            <div className="deepdive-how-label">── how it works ───────────────────────────────</div>
            <div className="deepdive-flow-caption">┌─ connect ────── capture ────── return ─┐</div>
            <pre className="deepdive-ascii-flow">{`
  ╭─────────────────────╮     ╭──────────────────────╮     ╭──────────────────────╮
  │  GUEST CONNECTS     │     │  BRANDED SPLASH      │     │  CONTACT CAPTURED    │
  │  ─────────────────  │     │  ──────────────────  │     │  ──────────────────  │
  │  Guest device       │────▶│  Branded splash page │────▶│  Email / social      │
  │  AP detects client  │     │  Venue logo + colors │     │  WhatsApp opt-in     │
  │  DNS redirect fires │     │  Login options ×4    │     │  GDPR consent logged │
  ╰─────────────────────╯     ╰──────────────────────╯     ╰──────────────────────╯
           │                           │                            │
  ╭─────────────────────╮     ╭──────────────────────╮     ╭──────────────────────╮
  │  ANY HARDWARE       │     │  ONE PLATFORM        │     │  YOUR BRAND ON IT    │
  │  ─────────────────  │     │  ──────────────────  │     │  ──────────────────  │
  │  Cisco / Aruba      │────▶│  Vendor-agnostic API │────▶│  White-label portal  │
  │  UniFi / Ruckus     │     │  RADIUS bridge       │     │  Own brand, own URL  │
  │  MikroTik / other   │     │  Session management  │     │  Resell to venues    │
  ╰─────────────────────╯     ╰──────────────────────╯     ╰──────────────────────╯
           │                           │                            │
  ╭─────────────────────╮     ╭──────────────────────╮     ╭──────────────────────╮
  │  VISIT PATTERNS     │     │  SMART FOLLOW-UP     │     │  GUESTS RETURN       │
  │  ─────────────────  │     │  ──────────────────  │     │  ──────────────────  │
  │  Device profile     │────▶│  Email sequence      │────▶│  Return rate tracked │
  │  Visit frequency    │     │  SMS / WhatsApp      │     │  Opt-in rate visible │
  │  Dwell time         │     │  Offer triggers      │     │  Cost per contact $  │
  ╰─────────────────────╯     ╰──────────────────────╯     ╰──────────────────────╯`}
            </pre>
            <div className="deepdive-value"><span className="vk">▸ why it matters</span> — guest wifi becomes a contact list you own. Every login is a guest you can invite back.</div>
          </div>
        </Reveal>

        {/* ── Longform ─────────────────────────────────────── */}
        <Reveal delay={80}>
          <div className="deepdive-longform" style={{ marginTop: 40 }}>
            <p>
              MyWiFi Networks launched in 2010 because hospitality venues were sitting on one of the most underused marketing surfaces imaginable: the WiFi login screen. Every guest connecting — at a hotel, café, airport lounge, or stadium — was a warm lead with a real device in hand and two seconds of attention to give. The only thing venues needed was a platform that could capture that moment, stay compliant, and connect to their existing email tools. That became MyWiFi.
            </p>
            <p>
              A resort group in the Middle East managing 12 properties runs MyWiFi across 6 hardware vendors without a single custom integration per site. Guests see a branded portal in the local language, connect via social login or email, and opt in at a 71% rate — well above industry norms. The marketing team uses the captured data to run post-stay re-engagement campaigns on WhatsApp and email. One campaign for a return-booking offer generated a 14% redemption rate on a list that cost them zero dollars to build.
            </p>
            <p>
              The platform's focus for the next 12 months is deepening the analytics layer and adding AI-driven segmentation: instead of sending the same re-engagement email to every opted-in guest, the system will predict which guests are high-value return candidates and trigger personalized sequences automatically. Resellers in Latin America and Southeast Asia are the primary growth markets — both regions have high WhatsApp penetration and an underserved hospitality tech market.
            </p>
          </div>
        </Reveal>

        {/* ── KPI strip ────────────────────────────────────── */}
        <Reveal delay={100}>
          <div style={{ marginTop: 36 }}>
            <ProjectKpiStrip kpis={[
              { label: "founded",       value: "2010" },
              { label: "countries",     value: "40+" },
              { label: "opt-in avg",    value: "71%" },
              { label: "hw vendors",    value: "6" }
            ]} />
          </div>
        </Reveal>
      </div>
    </section>
    </>
  );
}

/* captive-portal "splash" mock — venue brand, social login, fine print */
function CaptivePortal() {
  return (
    <div className="win" style={{ background: "var(--bg-1)" }}>
      <div className="win-title">
        <div className="lights"><span className="r" /><span className="a" /><span className="g" /></div>
        <span>guest device · splash.hotelsantorini.com</span>
        <span className="meta">202 OK</span>
      </div>
      <div style={{
        background: "linear-gradient(160deg, color-mix(in oklch, var(--amber) 12%, var(--bg-2)) 0%, var(--bg-2) 70%)",
        padding: "28px 22px",
        position: "relative",
        minHeight: 320
      }}>
        {/* venue brand */}
        <div style={{ fontSize: 10, color: "var(--fg-4)", letterSpacing: "0.28em", textTransform: "uppercase" }}>
          ── powered by mywifi ───────────
        </div>
        <div style={{ marginTop: 14, fontFamily: "var(--mono)", fontWeight: 700, fontSize: 22, letterSpacing: "-0.03em", color: "var(--fg)" }}>
          Hotel Santorini
        </div>
        <div style={{ fontSize: 11, color: "var(--fg-3)", marginTop: 4 }}>
          γ · Santorini, Greece · est. 1974
        </div>

        <div style={{ marginTop: 22, padding: "14px 14px 12px", border: "1px solid var(--line-strong)", background: "var(--bg-1)" }}>
          <div style={{ fontSize: 10, color: "var(--amber)", letterSpacing: "0.22em", textTransform: "uppercase", marginBottom: 10 }}>
            ▸ free wifi · 60 minutes
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            {["Continue with Email", "Continue with Google", "Continue with WhatsApp", "Voucher code"].map((opt, i) => (
              <div key={i} style={{
                padding: "10px 14px",
                border: "1px solid " + (i === 0 ? "var(--amber)" : "var(--line)"),
                background: i === 0 ? "color-mix(in oklch, var(--amber) 8%, transparent)" : "transparent",
                fontSize: 12, color: i === 0 ? "var(--amber)" : "var(--fg-2)",
                display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", gap: 10
              }}>
                <span>{opt}</span>
                <span style={{ color: "var(--fg-4)" }}>→</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 10, fontSize: 9, color: "var(--fg-4)", letterSpacing: "0.05em" }}>
            By connecting you accept the terms and the venue's marketing list. GDPR-compliant.
          </div>
        </div>

        {/* metric strip */}
        <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, fontSize: 10, color: "var(--fg-3)" }}>
          {[
            { k: "DEVICES TODAY", v: "284" },
            { k: "RETURN RATE",   v: "62%" },
            { k: "OPT-INS",       v: "71%" }
          ].map((m, i) => (
            <div key={i} style={{ border: "1px solid var(--line)", padding: "8px 10px", background: "var(--bg-1)" }}>
              <div style={{ fontSize: 9, color: "var(--fg-4)", letterSpacing: "0.2em", textTransform: "uppercase" }}>{m.k}</div>
              <div style={{ fontFamily: "var(--mono)", color: "var(--amber)", fontWeight: 700, fontSize: 16 }}>{m.v}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


/* ============================================================
   07 — Guest Networks · physical-space data layer (sensor query)
   ============================================================ */
function GuestNetworks({ activeProject }) {
  if (activeProject !== "all" && activeProject !== "guestnetworks") return null;
  const chapterMarker = activeProject === "all"
    ? <AsciiRule num="07" label="guestnetworks" />
    : null;
  const features = [
    { g: "[01]", t: "Vendor-Agnostic Ingest", d: "APs, cams, beacons → one event stream." },
    { g: "[02]", t: "Query in English", d: "AI agents translate to time-bucketed SQL." },
    { g: "[03]", t: "Real Metrics", d: "Footfall, dwell, return rate. Not page views." },
    { g: "[04]", t: "Privacy-First", d: "On-prem. Aggregates only. GDPR-ready." }
  ];

  return (
    <>
      {chapterMarker}
      <section id="guestnetworks" data-section="guestnetworks" data-screen-label="07 Guest Networks" className="section-pad"
        style={{ "--amber": "#FF1E56", "--amber-d": "#cc0033" }}>
        <div className="container">
          <SectionHead num="07" label="guest networks · physical-space data layer" glyph="pixel" />
          <KpiStrip
            stats={["15K+ locations", "99.99% uptime"]}
            deployedAt={1778186700000}
          />
          <DeepDiveAsciiPreview project="guestnetworks" color="#FF1E56" />

        <Reveal>
          <div className="cmd-grid">
            <div>
              <h2 className="h-section">
                Guest Networks.<br />
                <span className="dim2" style={{ fontWeight: 600 }}>{">"} the data layer for physical spaces.</span>
              </h2>
              <p className="lede mt-20" style={{ maxWidth: "48ch" }}>
                Every device in your venue, one queryable surface. Ask in English, get answers.
              </p>

              <VisitCTA href="https://guestnetworks.com" label="guestnetworks.com" note="· enterprise / venue ops" />

              <FeatureList items={features} />
            </div>

            <Reveal delay={120}>
              <SensorQuery />
            </Reveal>
          </div>
        </Reveal>

        {/* ── How it works ─────────────────────────────────── */}
        <Reveal delay={60}>
          <div className="deepdive-how" style={{ marginTop: 52 }}>
            <div className="deepdive-how-label">── how it works ───────────────────────────────</div>
            <div className="deepdive-flow-caption">┌─ sense ────── ask ────── act ─┐</div>
            <pre className="deepdive-ascii-flow">{`
  ╭─────────────────────╮     ╭──────────────────────╮     ╭──────────────────────╮
  │  RAW SIGNALS        │     │  CLEAN PIPELINE      │     │  PRIVATE BY DESIGN   │
  │  ─────────────────  │     │  ──────────────────  │     │  ──────────────────  │
  │  WiFi APs (12+ type)│────▶│  Vendor-agnostic API │────▶│  On-prem time-series │
  │  BLE beacons        │     │  Event normalizer    │     │  No PII stored       │
  │  IP cameras         │     │  Dedup + aggregate   │     │  GDPR-clean by design│
  ╰─────────────────────╯     ╰──────────────────────╯     ╰──────────────────────╯
           │                           │                            │
  ╭─────────────────────╮     ╭──────────────────────╮     ╭──────────────────────╮
  │  PLAIN ENGLISH      │     │  INSTANT QUERY       │     │  ANSWERS, NOT CSV    │
  │  ─────────────────  │     │  ──────────────────  │     │  ──────────────────  │
  │  Plain English ask  │────▶│  Intent → SQL        │────▶│  Bar chart / table   │
  │  "footfall by hour" │     │  1 query language    │     │  Anomaly flags       │
  │  "dwell in zone B"  │     │  Time-bucket resolve │     │  Trend deltas        │
  ╰─────────────────────╯     ╰──────────────────────╯     ╰──────────────────────╯
           │                           │                            │
  ╭─────────────────────╮     ╭──────────────────────╮     ╭──────────────────────╮
  │  LIVE FLOOR DATA    │     │  REAL-TIME ALERTS    │     │  ACTION TAKEN        │
  │  ─────────────────  │     │  ──────────────────  │     │  ──────────────────  │
  │  Device fingerprint │────▶│  Overcrowding alert  │────▶│  Staff reassignment  │
  │  Auth rules         │     │  Security anomaly    │     │  Layout decisions    │
  │  Network isolation  │     │  Bandwidth spike     │     │  Revenue attribution │
  ╰─────────────────────╯     ╰──────────────────────╯     ╰──────────────────────╯`}
            </pre>
            <div className="deepdive-value"><span className="vk">▸ why it matters</span> — your floor already generates the data. Now you can ask it questions in plain English.</div>
          </div>
        </Reveal>

        {/* ── Longform ─────────────────────────────────────── */}
        <Reveal delay={80}>
          <div className="deepdive-longform" style={{ marginTop: 40 }}>
            <p>
              Guest Networks started from one narrow but expensive problem: large venues — malls, stadiums, airports — were sitting on sensor data from dozens of hardware vendors that couldn't talk to each other. An operations manager who wanted to know footfall in Zone B during peak hours had to export from five separate dashboards and stitch CSV files together manually. The data was there. The answer was not. That gap needed one query layer, not five integrations.
            </p>
            <p>
              A regional shopping center in the UK running 12 sensor types — three WiFi AP vendors, two camera systems, BLE beacons, and a legacy door counter — connected all of them to Guest Networks in a single on-prem deploy. Within a week the operations team ran their first natural-language footfall query and got a clean hourly bar chart. The facility manager used the data to restructure security shift schedules around real peak hours rather than historic guesses. The first month saved 18 staff-hours of manual reporting.
            </p>
            <p>
              The roadmap prioritizes two expansions: an anomaly detection layer that fires real-time alerts when footfall or dwell patterns break historical baselines (early warning for crowd safety events), and a managed SaaS option for mid-market venues that want the analytics capability without the on-prem infrastructure overhead. Reseller partnerships with systems integrators in the UK and Australia are the primary go-to-market channel for the next fiscal year.
            </p>
          </div>
        </Reveal>

        {/* ── KPI strip ────────────────────────────────────── */}
        <Reveal delay={100}>
          <div style={{ marginTop: 36 }}>
            <ProjectKpiStrip kpis={[
              { label: "query language", value: "1" },
              { label: "sensor types",   value: "12+" },
              { label: "uptime SLA",     value: "99.7%" },
              { label: "deployment",     value: "on-prem" }
            ]} />
          </div>
        </Reveal>
      </div>
    </section>
    </>
  );
}

/* sensor-query "ask in english" terminal — query → bar chart */
function SensorQuery() {
  const hours = [
    { h: "11", v: 18 }, { h: "12", v: 42 }, { h: "13", v: 56 },
    { h: "14", v: 38 }, { h: "15", v: 24 }, { h: "16", v: 31 },
    { h: "17", v: 49 }, { h: "18", v: 78 }, { h: "19", v: 92 },
    { h: "20", v: 88 }, { h: "21", v: 64 }, { h: "22", v: 22 }
  ];
  const max = 100;
  return (
    <div className="terminal" style={{ background: "#08090a", border: "1px solid var(--line-strong)" }}>
      <div className="win-title">
        <div className="lights"><span className="r" /><span className="a" /><span className="g" /></div>
        <span>guestnet · query console</span>
        <span className="meta">conn · westfield-mall-01</span>
      </div>
      <div className="body" style={{ padding: 18, fontFamily: "var(--mono)", fontSize: 12.5, lineHeight: 1.65, color: "var(--fg-2)" }}>
        <div><span className="term-prompt">guestnet</span> <span className="term-amber">›</span> what was footfall yesterday by hour, main entrance only</div>
        <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>↻ resolving sensors · 12 nodes · range 11:00–22:00</div>
        <div><span className="term-ok">✓</span> <span className="dim">ap.main</span> · 1 ap</div>
        <div><span className="term-ok">✓</span> <span className="dim">cam.entry</span> · 2 cams</div>
        <div><span className="term-ok">✓</span> <span className="dim">beacon.foyer</span> · 4 ble</div>

        <div style={{ marginTop: 14, fontSize: 10, color: "var(--fg-4)", letterSpacing: "0.22em", textTransform: "uppercase" }}>
          ── unique devices · per hour ───────────
        </div>

        {/* bar chart */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 4, alignItems: "end", height: 110, marginTop: 10 }}>
          {hours.map((b, i) => {
            const peak = b.v >= 80;
            return (
              <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                <div style={{
                  width: "100%",
                  height: (b.v / max * 100) + "%",
                  background: peak ? "var(--amber)" : "color-mix(in oklch, var(--amber) 45%, var(--bg-3))",
                  border: "1px solid " + (peak ? "var(--amber)" : "var(--line-strong)"),
                  boxShadow: peak ? "0 0 12px color-mix(in oklch, var(--amber) 35%, transparent)" : "none"
                }} />
                <div style={{ fontSize: 9, color: peak ? "var(--amber)" : "var(--fg-4)" }}>{b.h}</div>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 14, padding: "10px 0 0", borderTop: "1px dashed var(--line)", fontSize: 11, color: "var(--fg-3)" }}>
          <span className="amber">▸</span> peak <span className="amber">19:00 · 92 devices</span> · dwell median 11.4 min ·
          <span style={{ color: "var(--green)" }}> +18% vs last tue</span>
        </div>
        <div className="mt-12"><span className="term-prompt">guestnet</span> <span className="term-amber">›</span><span className="cursor" /></div>
      </div>
    </div>
  );
}


/* ============================================================
   08 — OpenClaw + Hermes · agentic workforce (closer)
   ============================================================ */
function OpenClawHermes({ activeProject }) {
  if (activeProject !== "all" && activeProject !== "openclaw") return null;
  const chapterMarker = activeProject === "all"
    ? <AsciiRule num="08" label="openclaw" />
    : null;
  const features = [
    { g: "[01]", t: "Replace, Don't Augment", d: "Agents do the job, end-to-end. Audited." },
    { g: "[02]", t: "Multi-Channel", d: "Email, Slack, WhatsApp, voice, tickets." },
    { g: "[03]", t: "Agents Managing Agents", d: "OpenClaw, Hermes, Claude & Codex." },
    { g: "[04]", t: "AI Readiness Audit", d: "Per agent, all-in. Beats any FTE." }
  ];

  return (
    <>
      {chapterMarker}
      <section id="openclaw" data-section="openclaw" data-screen-label="08 OpenClaw + Hermes" className="section-pad"
        style={{ "--amber": "#F5A623", "--amber-d": "#c98010" }}>
        <div className="container">
          <SectionHead num="08" label="openclaw + hermes · agentic workforces" glyph="diamond" />
          <KpiStrip
            stats={["AI agent teams", "flat-rate subscriptions"]}
            deployedAt={1778189400000}
          />
          <DeepDiveAsciiPreview project="openclaw" color="#F5A623" />

        <Reveal>
          <div className="cmd-grid">
            <div>
              <h2 className="h-section">
                OpenClaw + Hermes.<br />
                <span className="dim2" style={{ fontWeight: 600 }}>{">"} when agents ARE the entire workforce.</span>
              </h2>
              <p className="lede mt-20" style={{ maxWidth: "48ch" }}>
                The stack I run my own companies on — now packaged for yours. Costs in dollars/day, not salaries/month.
              </p>

              <VisitCTA href="https://github.com/KevinZai/openclaw-automation" label="github · openclaw-automation" note="· book a workforce audit →" />

              <FeatureList items={features} />
            </div>

            <Reveal delay={120}>
              <AgentTopology />
            </Reveal>
          </div>
        </Reveal>

        {/* ── How it works ─────────────────────────────────── */}
        <Reveal delay={60}>
          <div className="deepdive-how" style={{ marginTop: 52 }}>
            <div className="deepdive-how-label">── how it works ───────────────────────────────</div>
            <div className="deepdive-flow-caption">┌─ listen ────── route ────── ship ─┐</div>
            <pre className="deepdive-ascii-flow">{`
  ╭─────────────────────╮     ╭──────────────────────╮     ╭──────────────────────╮
  │  EVERY CHANNEL      │     │  ONE SHARED MEMORY   │     │  NOTHING DROPPED     │
  │  ─────────────────  │     │  ──────────────────  │     │  ──────────────────  │
  │  Inbox / Slack / WA │────▶│  1.1 GB long-context │────▶│  Persistent state    │
  │  Voice calls        │     │  Cross-agent memory  │     │  Decision log        │
  │  Tickets / ops      │     │  Priority queue      │     │  Pattern library     │
  ╰─────────────────────╯     ╰──────────────────────╯     ╰──────────────────────╯
           │                           │                            │
  ╭─────────────────────╮     ╭──────────────────────╮     ╭──────────────────────╮
  │  SMART TRIAGE       │     │  RIGHT SPECIALIST    │     │  WORK SHIPPED        │
  │  ─────────────────  │     │  ──────────────────  │     │  ──────────────────  │
  │  Peer triage layer  │────▶│  Route to specialist │────▶│  Agent runs task     │
  │  Confidence gate    │     │  Parallel fan-out    │     │  Output verified     │
  │  Escalation rules   │     │  Cost-aware routing  │     │  Audit trail written │
  ╰─────────────────────╯     ╰──────────────────────╯     ╰──────────────────────╯
           │                           │                            │
  ╭─────────────────────╮     ╭──────────────────────╮     ╭──────────────────────╮
  │  FLEET HEALTH       │     │  COST GUARDS         │     │  ALWAYS ON           │
  │  ─────────────────  │     │  ──────────────────  │     │  ──────────────────  │
  │  Agents tracked     │────▶│  flat-rate subs      │────▶│  Always-on workers   │
  │  Health checks      │     │  Token budget guards │     │  −96% vs FTE cost    │
  │  Spawn / retire     │     │  Model swap on spike │     │  Restart on stall    │
  ╰─────────────────────╯     ╰──────────────────────╯     ╰──────────────────────╯`}
            </pre>
            <div className="deepdive-value"><span className="vk">▸ why it matters</span> — a workforce on flat-rate subscriptions: works 24/7, never quits, and writes its own audit trail.</div>
          </div>
        </Reveal>

        {/* ── Longform ─────────────────────────────────────── */}
        <Reveal delay={80}>
          <div className="deepdive-longform" style={{ marginTop: 40 }}>
            <p>
              OpenClaw started as personal infrastructure — a way to run multiple Claude Code agents simultaneously without the context window collisions and state loss that made multi-agent work unreliable. The real problem was that every agent framework at the time treated agents as isolated sessions. There was no memory layer that survived between sessions, no supervisor that could triage competing priorities, and no cost visibility. Building that stack for five companies to share became OpenClaw.
            </p>
            <p>
              The current production deployment runs AI agent teams across 8 channels: email, Slack, WhatsApp, Messenger, Telegram, voice transcription, ticketing, and internal ops. Hermes — the long-context memory layer — holds 1.1 GB of persistent session state so agents pick up exactly where they left off after a restart. The full fleet runs on flat-rate subscriptions — 2× Claude Max, Codex Pro, SuperGrok, Gemini, and OpenRouter — without metered API bills. When a worker stalls, Hermes restarts it.
            </p>
            <p>
              The next phase packages this stack for external teams: a guided installation that deploys a client's first 5 agents in under 4 hours, complete with monitoring dashboards and cost budgets. The core OpenClaw runtime stays open-source. The managed service layer — SLA, priority support, white-glove onboarding — is the commercial product. First external cohort targets 10 clients by end of Q3 2026.
            </p>
          </div>
        </Reveal>

        {/* ── KPI strip ────────────────────────────────────── */}
        <Reveal delay={100}>
          <div style={{ marginTop: 36 }}>
            <ProjectKpiStrip kpis={[
              { label: "LCM memory",     value: "1.1 GB" },
              { label: "channels",       value: "8" }
            ]} />
          </div>
        </Reveal>
      </div>
    </section>
    </>
  );
}

/* agent topology + cost summary — node graph in ASCII */
function AgentTopology() {
  return (
    <div className="win" style={{ background: "var(--bg-1)" }}>
      <div className="win-title">
        <div className="lights"><span className="r" /><span className="a" /><span className="g" /></div>
        <span>openclaw · live cluster</span>
        <span className="meta">fleet online</span>
      </div>
      <div style={{ padding: 18 }}>
        {/* topology ASCII */}
        <pre style={{ fontFamily: "var(--mono)", fontSize: 11, lineHeight: 1.4, color: "var(--fg-2)", margin: 0, whiteSpace: "pre", overflowX: "auto" }}>
{`           ┌──────────────┐
           │  HERMES · LCM│  ← 1.1 GB long-context
           │  ████████░░░░│     persistent memory
           └──────┬───────┘
                  │
       ┌──────────┼──────────┐
       ▼          ▼          ▼
   ┌───────┐ ┌───────┐ ┌───────┐
   │ SUPV  │ │ SUPV  │ │ SUPV  │   peer-triage layer
   │ ◆◆◆   │ │ ◆◆◆   │ │ ◆◆◆   │   (3 supervisors)
   └───┬───┘ └───┬───┘ └───┬───┘
       ▼         ▼         ▼
   ╔═══════════════════════════╗
   ║ AGENTS · ACTIVE           ║
   ║ ████████████████████████  ║
   ║ inbox · slack · wa · voice║
   ║ tickets · ops             ║
   ╚═══════════════════════════╝`}
        </pre>

        {/* cost panel */}
        <div className="deepdive-kpi-grid" style={{ marginTop: 16, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1, background: "var(--line)", border: "1px solid var(--line-strong)" }}>
          {[
            { k: "CHANNELS", v: "8",       d: "live · routed" },
            { k: "RECOVERY", v: "restart", d: "on stall" },
            { k: "BILLING",  v: "flat",    d: "subscriptions" },
            { k: "VS · FTE", v: "−96%",    d: "monthly burn" }
          ].map((kpi, i) => (
            <div key={i} style={{ background: "var(--bg-1)", padding: "12px 12px 10px" }}>
              <div style={{ fontSize: 9, color: "var(--fg-4)", letterSpacing: "0.2em", textTransform: "uppercase" }}>{kpi.k}</div>
              <div style={{ fontFamily: "var(--mono)", fontWeight: 700, fontSize: 18, color: i === 3 ? "var(--green)" : "var(--amber)", marginTop: 2, letterSpacing: "-0.04em" }}>{kpi.v}</div>
              <div style={{ fontSize: 9, color: "var(--fg-3)", marginTop: 2 }}>{kpi.d}</div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 12, padding: "10px 0 0", borderTop: "1px dashed var(--line)", display: "grid", gridTemplateColumns: "1fr auto", gap: 10, fontSize: 10, color: "var(--fg-4)", letterSpacing: "0.18em", textTransform: "uppercase" }}>
          <span><span className="amber">▸</span> last spawn · 14:22 · a trial agent</span>
          <span style={{ color: "var(--green)" }}>healthy</span>
        </div>
      </div>
    </div>
  );
}


/* expose to window so app.jsx can mount them */
Object.assign(window, {
  ProjectsTabbed,
  ProjectKpiStrip,
  ReadyIQ,
  DMHub,
  MyWiFi,
  GuestNetworks,
  OpenClawHermes
});

export {
  ProjectsTabbed,
  ProjectKpiStrip,
  ReadyIQ,
  DMHub,
  MyWiFi,
  GuestNetworks,
  OpenClawHermes
};
